import React, { useState, useEffect } from "react";
import { OrgXRefUser } from "src/@types/ImpiricaPlatformApi";
import { useApi } from "src/Api/useApi";
import { Button } from "src/components/buttons";
import { DataTable, ModalOverlay } from "src/components/common";
import { Icon } from "src/components/icons/Icon";
import { House, Lock } from "lucide-react";
import { Tooltip } from "antd";
import { ActionIcon } from "./ActionColumnIconsAndButton";
import { OrgSelector } from "./OrgSelector";
import Spinner from "src/components/common/Spinner";
import { toast } from "react-toastify";

interface IProps {
	orgId: number;
	userId: number;
}

export const UserOrganizationsGrid = (props: IProps) => {
	const { orgId, userId } = props;
	const { organizationClient } = useApi();

	const [orgsForUser, setOrgsForUser] = useState<OrgXRefUser[]>([]);
	const [showModal, setShowModal] = useState<boolean>(false);

	useEffect(() => {
		// Load Orgs for User
		_loadData();
	}, []);

	const _loadData = async () => {
		if (organizationClient) {
			const orgs = await organizationClient.getOrgXRefUsersByUser(userId);
			setOrgsForUser(orgs);
		}
	};

	const _addOrg = async (newOrgId: number) => {
		const newXRef: OrgXRefUser[] = [
			new OrgXRefUser({
				organizationId: newOrgId,
				impiricaUserId: userId,
				dateModified: new Date(),
				primaryOrg: false,
				isActive: true,
			}),
		];

		if (organizationClient) {
			await organizationClient.addOrgXRefUser(newXRef);
			const updatedOrgs = await organizationClient.getOrgXRefUsersByUser(
				userId
			);
			toast.success("Added organization");
			setOrgsForUser(updatedOrgs);
		}
	};

	const _removeOrg = async (row: OrgXRefUser) => {
		const removeXRef: OrgXRefUser[] = [
			new OrgXRefUser({
				organizationId: row.organizationId,
				impiricaUserId: row.impiricaUserId,
				dateModified: row.dateModified,
				primaryOrg: row.primaryOrg,
				isActive: row.isActive,
			}),
		];

		if (organizationClient) {
			await organizationClient.removeOrgXRefUser(removeXRef);
			const updatedOrgs = await organizationClient.getOrgXRefUsersByUser(
				userId
			);
			toast.success("Removed organization");
			setOrgsForUser(updatedOrgs);
		}
	};

	const _toggleModal = () => {
		setShowModal(!showModal);
	};

	const _getActions = (rowInfo: any) => {
		if (rowInfo.original.primaryOrg === true) {
			return (
				<>
					<Tooltip title="Home Organization" className={"form-icon"}>
						<Icon icon={House} />
					</Tooltip>
				</>
			);
		}

		if (rowInfo.original.organizationId === orgId) {
			return (
				<>
					<Tooltip title="Current Organization" className={"form-icon"}>
						<Icon icon={Lock} />
					</Tooltip>
				</>
			);
		}

		return (
			<>
				<ActionIcon
					title="Delete"
					type="delete"
					onClick={() => _removeOrg(rowInfo.original)}
				/>
			</>
		);
	};

	const _getColumns = () => {
		const columns = [
			{
				accessor: "id",
				show: false,
			},
			{
				Header: <Tooltip title="Organization Name">Organization</Tooltip>,
				accessor: "name",
				filterable: true,
			},
			{
				Header: <Tooltip title="Actions">Actions</Tooltip>,
				accessor: "Actions",
				Cell: (rowInfo: any) => {
					return _getActions(rowInfo);
				},
				id: "Actions",
			},
		];

		return columns;
	};

	return orgsForUser && orgsForUser.length > 0 ? (
		<>
			<div className="border-bottom mb-3">
				<h3>Organizations</h3>
			</div>
			<div>
				<DataTable data={orgsForUser} columns={_getColumns()} sortable={true} />
			</div>
			<div style={{ marginBottom: 25 }}>
				<Button
					key={"addOrgXref"}
					className="btn-primary mr-3"
					onClick={_toggleModal}
					type="button"
				>
					Add Org
				</Button>
			</div>
			<ModalOverlay
				isOpen={showModal}
				modalSize="modal-lg"
				headerChildren={"Select Organization"}
				children={
					<OrgSelector
						key={"OrgSelector"}
						orgIdFilter={[
							1,
							orgId,
							...orgsForUser.map((o: OrgXRefUser) => {
								return o.organizationId;
							}),
						]}
						onSelect={_addOrg}
					/>
				}
				onRequestClose={_toggleModal}
			/>
		</>
	) : (
		<div
			style={{
				display: "flex",
				flex: 1,
				flexDirection: "row",
			}}
		>
			<div
				style={{
					flex: 1,
				}}
			>
				<Spinner />
			</div>
			<div
				style={{
					flex: 12,
				}}
			>
				Loading Organizations...
			</div>
		</div>
	);
};
